import { useState, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import JSZip from 'jszip';
import { apiGet, apiUpload } from '@/lib/api';
import { showToast } from '@/components/Toast';
import { FileNode } from '@/lib/fileUtils';

/**
 * 递归读取文件夹 entry 中的所有文件
 * 使用 any 类型避免 IDE 对 FileSystemDirectoryEntry 等 Web API 类型的识别问题
 * 返回 { relativePath, file } 数组，relativePath 保留文件夹层级结构
 */
function readAllEntriesFromDirectory(
  dirEntry: any,
  basePath: string = ''
): Promise<{ relativePath: string; file: File }[]> {
  return new Promise((resolve, reject) => {
    const reader = dirEntry.createReader();
    const allResults: { relativePath: string; file: File }[] = [];

    // readEntries 每次最多返回 100 条，需要循环调用直到返回空数组
    const readBatch = () => {
      reader.readEntries(async (entries: any[]) => {
        if (entries.length === 0) {
          resolve(allResults);
          return;
        }

        for (const entry of entries) {
          const entryPath = basePath ? `${basePath}/${entry.name}` : entry.name;
          if (entry.isFile) {
            const file = await new Promise<File>((res, rej) => {
              entry.file(res, rej);
            });
            allResults.push({ relativePath: entryPath, file });
          } else if (entry.isDirectory) {
            const subFiles = await readAllEntriesFromDirectory(
              entry,
              entryPath
            );
            allResults.push(...subFiles);
          }
        }

        // 继续读取下一批
        readBatch();
      }, reject);
    };

    readBatch();
  });
}

// 文件夹压缩大小限制
const ONE_GB = 1 * 1024 * 1024 * 1024; // 1GB
const RAW_SIZE_SAFETY_LIMIT = 3 * 1024 * 1024 * 1024; // 3GB 原始大小安全上限，防止浏览器内存溢出崩溃

/**
 * 压缩进度回调类型
 * @param phase - 当前阶段: 'reading' 读取文件 | 'compressing' 生成 zip
 * @param progress - 进度百分比 0-100
 * @param detail - 当前处理的文件名/描述
 */
type CompressionProgressCallback = (
  phase: 'reading' | 'compressing',
  progress: number,
  detail?: string
) => void;

/**
 * 将文件夹 entry 打包为 zip File 对象
 * 逐步加载文件并检查大小：
 * 1. 逐文件加载，跟踪累计原始大小，超过安全上限（4GB）立即中止防止浏览器崩溃
 * 2. 压缩完成后检查实际压缩大小，超过 1GB 拒绝上传
 * 3. 通过 onProgress 回调报告压缩进度
 */
async function zipFolderEntries(
  folderEntry: any,
  onProgress?: CompressionProgressCallback
): Promise<File> {
  onProgress?.('reading', 0, 'Scanning folder...');
  const files = await readAllEntriesFromDirectory(folderEntry);
  const zip = new JSZip();
  const totalFiles = files.length;

  let cumulativeRawSize = 0;

  for (let i = 0; i < files.length; i++) {
    const { relativePath, file } = files[i];
    cumulativeRawSize += file.size;

    // 报告读取/添加文件进度（占总进度的 0-80%）
    const readProgress = Math.floor(((i + 1) / totalFiles) * 80);
    const fileName = relativePath.split('/').pop() || relativePath;
    onProgress?.('reading', readProgress, fileName);

    // 安全上限：防止浏览器因加载过多文件内容到内存而崩溃
    if (cumulativeRawSize > RAW_SIZE_SAFETY_LIMIT) {
      const sizeInGB = (cumulativeRawSize / ONE_GB).toFixed(2);
      throw new Error(`FOLDER_RAW_TOO_LARGE:${folderEntry.name}:${sizeInGB}`);
    }
    const arrayBuffer = await file.arrayBuffer();
    zip.file(relativePath, arrayBuffer);
  }

  // 报告压缩生成阶段进度（80-100%）
  onProgress?.('compressing', 80, 'Generating zip...');
  const blob = await zip.generateAsync({ type: 'blob' }, metadata => {
    // JSZip generateAsync 的进度回调：metadata.percent 是 0-100
    const zipProgress = 80 + Math.floor((metadata.percent / 100) * 20);
    onProgress?.('compressing', Math.min(zipProgress, 99));
  });

  onProgress?.('compressing', 100);

  // 检查压缩后的实际大小
  if (blob.size > ONE_GB) {
    const sizeInGB = (blob.size / ONE_GB).toFixed(2);
    throw new Error(
      `FOLDER_COMPRESSED_TOO_LARGE:${folderEntry.name}:${sizeInGB}`
    );
  }

  return new File([blob], `${folderEntry.name}.zip`, {
    type: 'application/zip',
  });
}

export interface ConflictFilesState {
  conflicting: File[];
  nonConflicting: File[];
  targetPath: string;
}

export interface UseFileUploadWithProgressOptions {
  projectId: string;
  getCurrentFolderId: () => string | null;
  resetProjectFilesCache: (projectId: string) => void;
  loadProjectFilesFromStore: (projectId: string) => Promise<void>;
}

export interface UseFileUploadWithProgressReturn {
  // State
  isUploadingToFolder: boolean;
  isDraggingOver: boolean;
  conflictFiles: ConflictFilesState | null;
  isConflictModalOpen: boolean;
  isCheckingConflicts: boolean;
  uploadProgress: number;
  isUploading: boolean;
  // Compression progress state (文件夹压缩进度)
  isCompressing: boolean;
  compressionProgress: number;
  compressionDetail: string;
  uploadInputRef: React.RefObject<HTMLInputElement>;

  // Actions
  handleDragOver: (e: React.DragEvent<HTMLDivElement>) => void;
  handleDragLeave: (e: React.DragEvent<HTMLDivElement>) => void;
  handleDrop: (e: React.DragEvent<HTMLDivElement>) => Promise<void>;
  handleUploadFiles: (
    event: React.ChangeEvent<HTMLInputElement>
  ) => Promise<void>;
  handleConflictOverwrite: () => Promise<void>;
  handleConflictSkip: () => Promise<void>;
  handleConflictCancel: () => void;
  performUpload: (
    files: File[],
    targetPath: string,
    skipConflictCheck?: boolean
  ) => Promise<void>;
}

export function useFileUploadWithProgress({
  projectId,
  getCurrentFolderId,
  resetProjectFilesCache,
  loadProjectFilesFromStore,
}: UseFileUploadWithProgressOptions): UseFileUploadWithProgressReturn {
  const { t } = useTranslation();

  // Upload files state
  const [isUploadingToFolder, setIsUploadingToFolder] = useState(false);
  const uploadInputRef = useRef<HTMLInputElement>(null);
  const [isDraggingOver, setIsDraggingOver] = useState(false);

  // File conflict confirmation state
  const [conflictFiles, setConflictFiles] = useState<ConflictFilesState | null>(
    null
  );
  const [isConflictModalOpen, setIsConflictModalOpen] = useState(false);
  const [isCheckingConflicts, setIsCheckingConflicts] = useState(false);

  // Upload progress state
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isUploading, setIsUploading] = useState(false);

  // Compression progress state (文件夹压缩进度)
  const [isCompressing, setIsCompressing] = useState(false);
  const [compressionProgress, setCompressionProgress] = useState(0);
  const [compressionDetail, setCompressionDetail] = useState('');
  const progressTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const uploadCompletedRef = useRef(false);

  // 启动模拟进度条
  const startUploadProgress = useCallback((fileCount: number) => {
    setIsUploading(true);
    setUploadProgress(0);
    uploadCompletedRef.current = false;

    // 每个文件2秒，总时间
    const totalTime = Math.max(fileCount * 2000, 2000); // 至少 2 秒
    const updateInterval = 100; // 每 100ms 更新一次，更平滑
    const maxProgress = 99; // 最多到 99%

    let currentProgress = 0;
    const startTime = Date.now();

    progressTimerRef.current = setInterval(() => {
      if (uploadCompletedRef.current) {
        // 如果上传完成，平滑过渡到 100%
        setUploadProgress(100);
        if (progressTimerRef.current) {
          clearInterval(progressTimerRef.current);
          progressTimerRef.current = null;
        }
        // 500ms 后隐藏进度条
        setTimeout(() => {
          setIsUploading(false);
          setUploadProgress(0);
        }, 500);
        return;
      }

      // 计算经过的时间和进度
      const elapsedTime = Date.now() - startTime;
      const progressRatio = Math.min(elapsedTime / totalTime, 1);

      // 使用缓动函数让进度更自然
      const easedProgress = progressRatio * (2 - progressRatio); // 二次缓动
      currentProgress = Math.min(easedProgress * maxProgress, maxProgress);

      setUploadProgress(Math.floor(currentProgress));
    }, updateInterval);
  }, []);

  // 停止进度条
  const stopUploadProgress = useCallback(() => {
    uploadCompletedRef.current = true;
  }, []);

  // 处理拖拽进入
  const handleDragOver = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    // 阻止浏览器默认行为（打开文件）
    if (e.dataTransfer) {
      e.dataTransfer.dropEffect = 'copy';
    }
    // 只在拖拽文件时显示效果，排除拖拽文本等
    if (e.dataTransfer.types.includes('Files')) {
      setIsDraggingOver(true);
    }
  }, []);

  // 处理拖拽离开
  const handleDragLeave = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    // 检查是否真的离开了拖拽区域（不是进入子元素）
    const rect = e.currentTarget.getBoundingClientRect();
    const x = e.clientX;
    const y = e.clientY;

    if (
      x <= rect.left ||
      x >= rect.right ||
      y <= rect.top ||
      y >= rect.bottom
    ) {
      setIsDraggingOver(false);
    }
  }, []);

  // 检查文件是否有重名冲突
  const checkFileConflicts = useCallback(
    async (
      files: File[],
      targetPath: string
    ): Promise<{ conflicting: File[]; nonConflicting: File[] }> => {
      try {
        // 重新获取当前项目文件列表
        const response = await apiGet<any>(
          `/api/projects/${projectId}/files?recursive=true`
        );
        if (response.code !== 200) {
          throw new Error('Failed to fetch project files');
        }

        const remoteFiles = response?.files || [];

        // 获取目标路径下的现有文件名
        const existingFileNames = new Set<string>();

        // 递归查找目标路径下的所有文件
        const findFilesInPath = (nodes: FileNode[], path: string) => {
          if (path === '/' || path === '') {
            // 根目录
            nodes.forEach(node => {
              if (node.type === 'file') {
                existingFileNames.add(node.name);
              }
            });
          } else {
            // 子目录
            const pathParts = path.split('/').filter(p => p);
            let currentNodes = nodes;

            for (const part of pathParts) {
              const folder = currentNodes.find(
                n => n.type === 'folder' && n.name === part
              );
              if (!folder || !folder.children) return;
              currentNodes = folder.children;
            }

            currentNodes.forEach(node => {
              if (node.type === 'file') {
                existingFileNames.add(node.name);
              }
            });
          }
        };

        findFilesInPath(remoteFiles, targetPath);

        // 分类文件：冲突和非冲突
        const conflicting: File[] = [];
        const nonConflicting: File[] = [];

        files.forEach(file => {
          if (existingFileNames.has(file.name)) {
            conflicting.push(file);
          } else {
            nonConflicting.push(file);
          }
        });

        return { conflicting, nonConflicting };
      } catch (error) {
        console.error('Error checking file conflicts:', error);
        // 如果检测失败，保守起见认为所有文件都可能冲突
        return { conflicting: files, nonConflicting: [] };
      }
    },
    [projectId]
  );

  // 执行文件上传（带冲突检测）
  const performUpload = useCallback(
    async (files: File[], targetPath: string, skipConflictCheck = false) => {
      if (files.length === 0) return;

      // 如果不跳过冲突检测，先检查是否有重名
      if (!skipConflictCheck) {
        setIsCheckingConflicts(true);
        try {
          const { conflicting, nonConflicting } = await checkFileConflicts(
            files,
            targetPath
          );

          if (conflicting.length > 0) {
            // 有冲突，显示确认对话框
            setConflictFiles({ conflicting, nonConflicting, targetPath });
            setIsConflictModalOpen(true);
            setIsCheckingConflicts(false);
            return;
          }
          // 无冲突，继续上传
        } catch (error) {
          console.error('Conflict check failed:', error);
          // 检测失败，继续上传（保持原有行为）
        } finally {
          setIsCheckingConflicts(false);
        }
      }

      // 执行上传
      setIsUploadingToFolder(true);
      startUploadProgress(files.length);

      try {
        const formData = new FormData();
        formData.append('replace', 'true');
        files.forEach(file => {
          formData.append('files', file);
        });

        if (targetPath && targetPath !== '/') {
          formData.append('parentFolderPath', targetPath);
        }

        const data = await apiUpload<any>(
          `/api/projects/${projectId}/files`,
          formData
        );

        if (data.code === 200) {
          stopUploadProgress();
          resetProjectFilesCache(projectId);
          await loadProjectFilesFromStore(projectId);

          // 检查每个文件的上传结果
          const results = data?.results || [];
          const failedFiles = results.filter((r: any) => r.code !== 200);

          if (failedFiles.length > 0) {
            // 整理失败信息
            const failedList = failedFiles
              .map(
                (failed: any) =>
                  `• ${failed.fileName ? failed.fileName + ': ' : ''}${failed.message || 'Unknown error'}`
              )
              .join('\n');

            const successCount = results.length - failedFiles.length;
            if (successCount > 0) {
              // 部分成功
              showToast(
                t('toast.partialUploadSuccess', {
                  successCount,
                  failedCount: failedFiles.length,
                  failedList,
                }),
                'warning',
                12000
              );
            } else {
              // 全部失败
              showToast(
                t('toast.allFilesUploadFailed', { failedList }),
                'error',
                10000
              );
            }
          } else {
            // 全部成功
            showToast(
              t('toast.filesUploadedSuccessfully', { count: files.length }),
              'success'
            );
          }
        } else {
          stopUploadProgress();
          console.error('Failed to upload files:', data);
          showToast(data.message || 'Failed to upload files', 'error');
        }
      } catch (error: any) {
        stopUploadProgress();
        console.error('Failed to upload files:', error);

        let errorMessage = 'Failed to upload files';

        if (
          error.code === 'ECONNABORTED' ||
          error.message?.includes('timeout')
        ) {
          errorMessage =
            'Upload timeout. Please try uploading smaller files or check your network connection.';
        } else if (error.code === 'ERR_NETWORK') {
          errorMessage =
            'Network error. Please check your internet connection and try again.';
        } else if (error.response?.status === 413) {
          errorMessage =
            'File size exceeds server limit. Please upload smaller files.';
        } else if (error.response?.data?.message) {
          errorMessage = error.response.data.message;
        } else if (error.message) {
          errorMessage = error.message;
        }

        showToast(errorMessage, 'error', 8000);
      } finally {
        setIsUploadingToFolder(false);
        if (uploadInputRef.current) {
          uploadInputRef.current.value = '';
        }
      }
    },
    [
      projectId,
      checkFileConflicts,
      startUploadProgress,
      stopUploadProgress,
      resetProjectFilesCache,
      loadProjectFilesFromStore,
      t,
    ]
  );

  // 处理冲突确认：覆盖
  const handleConflictOverwrite = useCallback(async () => {
    if (!conflictFiles) return;
    setIsConflictModalOpen(false);

    // 上传所有文件（包括冲突的）
    const allFiles = [
      ...conflictFiles.conflicting,
      ...conflictFiles.nonConflicting,
    ];
    await performUpload(allFiles, conflictFiles.targetPath, true);
    setConflictFiles(null);
  }, [conflictFiles, performUpload]);

  // 处理冲突确认：跳过重名文件
  const handleConflictSkip = useCallback(async () => {
    if (!conflictFiles) return;
    setIsConflictModalOpen(false);

    // 只上传非冲突文件
    if (conflictFiles.nonConflicting.length > 0) {
      await performUpload(
        conflictFiles.nonConflicting,
        conflictFiles.targetPath,
        true
      );
    } else {
      showToast(t('toast.noFilesToUploadAfterSkipping'), 'info');
    }
    setConflictFiles(null);
  }, [conflictFiles, performUpload, t]);

  // 处理冲突取消
  const handleConflictCancel = useCallback(() => {
    setIsConflictModalOpen(false);
    setConflictFiles(null);
    if (uploadInputRef.current) {
      uploadInputRef.current.value = '';
    }
  }, []);

  // 处理文件拖放（支持文件夹：自动压缩为 zip 后上传）
  const handleDrop = useCallback(
    async (e: React.DragEvent<HTMLDivElement>) => {
      e.preventDefault();
      e.stopPropagation();
      // 强制阻止浏览器默认行为（打开/下载文件）
      if (e.dataTransfer) {
        e.dataTransfer.dropEffect = 'copy';
      }
      setIsDraggingOver(false);

      const items = e.dataTransfer.items;
      const regularFiles: File[] = [];
      const folderEntries: any[] = [];

      // 🔥 区分文件和文件夹
      if (items) {
        for (let i = 0; i < items.length; i++) {
          const item = items[i];
          if (item.kind === 'file') {
            const entry = item.webkitGetAsEntry?.();
            if (entry && entry.isDirectory) {
              folderEntries.push(entry);
            } else {
              const file = item.getAsFile();
              if (file) regularFiles.push(file);
            }
          }
        }
      } else {
        // 回退：不支持 webkitGetAsEntry 的浏览器
        regularFiles.push(...Array.from(e.dataTransfer.files));
      }

      if (regularFiles.length === 0 && folderEntries.length === 0) return;

      // 🔥 将文件夹压缩为 zip 文件（带进度反馈）
      const zipFiles: File[] = [];
      if (folderEntries.length > 0) {
        setIsCompressing(true);
        setCompressionProgress(0);
        setCompressionDetail('');

        for (let fi = 0; fi < folderEntries.length; fi++) {
          const folderEntry = folderEntries[fi];
          // 多个文件夹时，计算每个文件夹的进度偏移量
          const folderBaseProgress = Math.floor(
            (fi / folderEntries.length) * 100
          );
          const folderProgressRange = Math.floor(100 / folderEntries.length);

          setCompressionDetail(folderEntry.name);

          try {
            const zipFile = await zipFolderEntries(
              folderEntry,
              (phase, progress, detail) => {
                // 将单个文件夹的进度映射到总进度
                const totalProgress =
                  folderBaseProgress +
                  Math.floor((progress / 100) * folderProgressRange);
                console.log(phase);
                setCompressionProgress(Math.min(totalProgress, 99));
                if (detail) {
                  setCompressionDetail(`${folderEntry.name}: ${detail}`);
                }
              }
            );
            zipFiles.push(zipFile);
          } catch (error: any) {
            const errorMsg = error?.message || '';
            if (errorMsg.startsWith('FOLDER_RAW_TOO_LARGE:')) {
              const [, name, size] = errorMsg.split(':');
              showToast(
                t('toast.folderTooLarge', {
                  name,
                  size,
                  defaultValue: `Folder "${name}" is too large (${size} GB). Maximum allowed size is 1 GB.`,
                }),
                'error'
              );
            } else if (errorMsg.startsWith('FOLDER_COMPRESSED_TOO_LARGE:')) {
              const [, name, size] = errorMsg.split(':');
              showToast(
                t('toast.folderTooLarge', {
                  name,
                  size,
                  defaultValue: `Folder "${name}" is too large (${size} GB after compression). Maximum allowed size is 1 GB.`,
                }),
                'error'
              );
            } else {
              console.error(
                `Failed to compress folder "${folderEntry.name}":`,
                error
              );
              showToast(
                t('toast.folderCompressFailed', {
                  name: folderEntry.name,
                  defaultValue: `Failed to compress folder "${folderEntry.name}". Please try uploading a zip file manually.`,
                }),
                'error'
              );
            }
          }
        }

        setIsCompressing(false);
        setCompressionProgress(0);
        setCompressionDetail('');
      }

      const allFiles = [...regularFiles, ...zipFiles];
      if (allFiles.length === 0) return;

      const targetPath = getCurrentFolderId() || '/';
      await performUpload(allFiles, targetPath, false);
    },
    [getCurrentFolderId, performUpload, t]
  );

  // 上传文件到当前文件夹
  const handleUploadFiles = useCallback(
    async (event: React.ChangeEvent<HTMLInputElement>) => {
      const filesList = Array.from(event.target.files || []);
      if (filesList.length === 0) return;

      const targetPath = getCurrentFolderId() || '/';
      await performUpload(filesList, targetPath, false);

      // 清空 input，允许重新选择相同文件
      if (uploadInputRef.current) {
        uploadInputRef.current.value = '';
      }
    },
    [getCurrentFolderId, performUpload]
  );

  return {
    // State
    isUploadingToFolder,
    isDraggingOver,
    conflictFiles,
    isConflictModalOpen,
    isCheckingConflicts,
    uploadProgress,
    isUploading,
    isCompressing,
    compressionProgress,
    compressionDetail,
    uploadInputRef,

    // Actions
    handleDragOver,
    handleDragLeave,
    handleDrop,
    handleUploadFiles,
    handleConflictOverwrite,
    handleConflictSkip,
    handleConflictCancel,
    performUpload,
  };
}
